import React, { useState, useRef } from 'react';
import api from '../../services/api';
import {
  Paperclip, MapPin, User, Trash2, Plus, Upload, ExternalLink, Phone, Briefcase,
  X, FileImage, Film, Music, FileText, Pencil, Check,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function tipoIcon(tipo) {
  if (tipo === 'imagem') return <FileImage size={14} className="text-blue-500" />;
  if (tipo === 'video') return <Film size={14} className="text-purple-500" />;
  if (tipo === 'audio') return <Music size={14} className="text-green-500" />;
  if (tipo === 'pdf') return <FileText size={14} className="text-red-500" />;
  return <Paperclip size={14} className="text-gray-400" />;
}

function tipoBadge(tipo) {
  const map = {
    imagem: 'bg-blue-100 text-blue-700',
    video: 'bg-purple-100 text-purple-700',
    audio: 'bg-green-100 text-green-700',
    pdf: 'bg-red-100 text-red-700',
    documento: 'bg-gray-100 text-gray-600',
  };
  return map[tipo] || map.documento;
}

function fmtBytes(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(0) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}

// ── Seção: Materiais de Apoio ─────────────────────────────────────────────────

function MateriaisSection({ botKey, materiais, empreendimentos = [], onChanged }) {
  const fileRef = useRef(null);
  const [nome, setNome] = useState('');
  const [empId, setEmpId] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [erro, setErro] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editNome, setEditNome] = useState('');
  const [editEmpId, setEditEmpId] = useState('');
  const [saving, setSaving] = useState(false);

  const handleFile = (e) => { setFile(e.target.files[0] || null); setErro(''); e.target.value = ''; };

  const handleAdd = async () => {
    if (!nome.trim() || !file) return;
    setUploading(true); setErro(''); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('nome', nome.trim());
      fd.append('arquivo', file);
      if (empId) fd.append('empreendimento_id', empId);
      await api.post(`/automacoes/configs/${botKey}/materiais`, fd, {
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      setNome(''); setFile(null); setEmpId(''); setProgress(0);
      await onChanged();
    } catch (err) {
      setErro(err.response?.data?.error || err.message || 'Erro ao enviar material');
      setProgress(0);
    }
    setUploading(false);
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditNome(m.nome);
    setEditEmpId(m.empreendimento_id || '');
  };

  const cancelEdit = () => { setEditingId(null); setEditNome(''); setEditEmpId(''); };

  const handleSaveEdit = async (id) => {
    setSaving(true);
    try {
      await api.patch(`/automacoes/materiais/${id}`, { nome: editNome.trim(), empreendimento_id: editEmpId || null });
      cancelEdit();
      await onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao salvar');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este material?')) return;
    try { await api.delete(`/automacoes/materiais/${id}`); onChanged(); } catch {}
  };

  const empMap = Object.fromEntries(empreendimentos.map(e => [e.id, e.nome]));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Paperclip size={15} className="text-orange-500" />
        <span className="text-sm font-semibold text-gray-700">Materiais de Apoio</span>
        <span className="text-xs text-gray-400">(PDFs, imagens, vídeos — o bot seleciona e envia o material certo pelo contexto)</span>
      </div>

      {materiais.length > 0 && (
        <div className="space-y-2">
          {materiais.map(m => (
            <div key={m.id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Linha normal */}
              <div className="flex items-center gap-3 bg-gray-50 px-3 py-2">
                {tipoIcon(m.tipo)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{m.nome}</p>
                  <p className="text-xs text-gray-400 truncate">{m.nome_original}{m.tamanho ? ` · ${fmtBytes(m.tamanho)}` : ''}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${m.empreendimento_id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                  {m.empreendimento_nome || empMap[m.empreendimento_id] || 'Geral'}
                </span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tipoBadge(m.tipo)}`}>{m.tipo.toUpperCase()}</span>
                <button onClick={() => editingId === m.id ? cancelEdit() : startEdit(m)} className="p-1 text-gray-300 hover:text-orange-500 transition-colors" title="Editar"><Pencil size={14} /></button>
                <button onClick={() => handleDelete(m.id)} className="p-1 text-gray-300 hover:text-red-500 transition-colors" title="Remover"><Trash2 size={14} /></button>
              </div>
              {/* Painel de edição inline */}
              {editingId === m.id && (
                <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border-t border-orange-100">
                  <input
                    value={editNome} onChange={e => setEditNome(e.target.value)}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                    placeholder="Nome do material"
                  />
                  <select
                    value={editEmpId} onChange={e => setEditEmpId(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                  >
                    <option value="">Geral (todos)</option>
                    {empreendimentos.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                  </select>
                  <button onClick={() => handleSaveEdit(m.id)} disabled={saving || !editNome.trim()} className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-40 whitespace-nowrap">
                    <Check size={13} /> Salvar
                  </button>
                  <button onClick={cancelEdit} className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"><X size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-start">
        <input
          value={nome} onChange={e => { setNome(e.target.value); setErro(''); }}
          placeholder="Nome descritivo (ex: Planta 79m² - 3 dorm. Ingarden)"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
        />
        <select
          value={empId} onChange={e => setEmpId(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
        >
          <option value="">Geral (todos)</option>
          {empreendimentos.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        <button onClick={() => fileRef.current?.click()} className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm transition-colors whitespace-nowrap ${file ? 'bg-green-50 border-green-300 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
          <Upload size={14} /> {file ? (file.name.length > 16 ? file.name.slice(0, 16) + '…' : file.name) : 'Arquivo'}
        </button>
        <button onClick={handleAdd} disabled={uploading || !nome.trim() || !file} className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-40 whitespace-nowrap">
          <Plus size={14} /> {uploading ? `${progress}%…` : 'Adicionar'}
        </button>
      </div>
      <input ref={fileRef} type="file" className="hidden" accept=".pdf,image/*,video/*,audio/*,.doc,.docx" onChange={handleFile} />

      {uploading && progress > 0 && (
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div className="bg-orange-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}

      {erro && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <X size={14} className="shrink-0" /> {erro}
        </div>
      )}
    </div>
  );
}

// ── Seção: Localizações ───────────────────────────────────────────────────────

function LocalizacoesSection({ botKey, localizacoes, onChanged }) {
  const [form, setForm] = useState({ nome: '', url: '', descricao: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!form.nome.trim() || !form.url.trim()) return;
    setSaving(true);
    try {
      await api.post(`/automacoes/configs/${botKey}/localizacoes`, form);
      setForm({ nome: '', url: '', descricao: '' });
      onChanged();
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta localização?')) return;
    try { await api.delete(`/automacoes/localizacoes/${id}`); onChanged(); } catch {}
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <MapPin size={15} className="text-blue-500" />
        <span className="text-sm font-semibold text-gray-700">Localizações</span>
        <span className="text-xs text-gray-400">(links Google Maps que o bot compartilha com o lead)</span>
      </div>

      {localizacoes.length > 0 && (
        <div className="space-y-2">
          {localizacoes.map(l => (
            <div key={l.id} className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <MapPin size={14} className="text-blue-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">{l.nome}</p>
                {l.descricao && <p className="text-xs text-gray-500">{l.descricao}</p>}
                <a href={l.url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1 truncate">
                  <ExternalLink size={10} /> {l.url.length > 50 ? l.url.slice(0, 50) + '…' : l.url}
                </a>
              </div>
              <button onClick={() => handleDelete(l.id)} className="p-1 text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <input value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Nome (ex: Localização Ingarden)" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
        <input value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="URL do Google Maps" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
        <input value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} placeholder="Descrição opcional (ex: Próximo ao Shopping)" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 col-span-1" />
        <button onClick={handleAdd} disabled={saving || !form.nome.trim() || !form.url.trim()} className="flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-40">
          <Plus size={14} /> {saving ? 'Salvando…' : 'Adicionar'}
        </button>
      </div>
    </div>
  );
}

// ── Seção: Contatos para Envio ────────────────────────────────────────────────

function ContatosSection({ botKey, contatos, onChanged }) {
  const [form, setForm] = useState({ nome: '', telefone: '', cargo: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!form.nome.trim() || !form.telefone.trim()) return;
    setSaving(true);
    try {
      await api.post(`/automacoes/configs/${botKey}/contatos`, form);
      setForm({ nome: '', telefone: '', cargo: '' });
      onChanged();
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este contato?')) return;
    try { await api.delete(`/automacoes/contatos/${id}`); onChanged(); } catch {}
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <User size={15} className="text-green-600" />
        <span className="text-sm font-semibold text-gray-700">Contatos para Envio</span>
        <span className="text-xs text-gray-400">(o bot envia o contato ao lead quando solicitado)</span>
      </div>

      {contatos.length > 0 && (
        <div className="space-y-2">
          {contatos.map(ct => (
            <div key={ct.id} className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center shrink-0">
                <User size={14} className="text-green-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800">{ct.nome}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  {ct.cargo && <span className="flex items-center gap-1"><Briefcase size={10} />{ct.cargo}</span>}
                  <span className="flex items-center gap-1"><Phone size={10} />{ct.telefone}</span>
                </div>
              </div>
              <button onClick={() => handleDelete(ct.id)} className="p-1 text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <input value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Nome completo" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300" />
        <input value={form.telefone} onChange={e => setForm(p => ({ ...p, telefone: e.target.value }))} placeholder="Telefone (ex: 11999999999)" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300" />
        <input value={form.cargo} onChange={e => setForm(p => ({ ...p, cargo: e.target.value }))} placeholder="Cargo (ex: Gerente Comercial)" className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300" />
      </div>
      <button onClick={handleAdd} disabled={saving || !form.nome.trim() || !form.telefone.trim()} className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40">
        <Plus size={14} /> {saving ? 'Salvando…' : 'Adicionar Contato'}
      </button>
    </div>
  );
}

// ── Wrapper: Base de Conhecimento de um bot ───────────────────────────────────

const ACCENT_CLASSES = {
  orange: { border: 'border-orange-200', header: 'bg-orange-50 border-orange-200', icon: 'text-orange-500' },
  pink: { border: 'border-pink-200', header: 'bg-pink-50 border-pink-200', icon: 'text-pink-500' },
  blue: { border: 'border-blue-200', header: 'bg-blue-50 border-blue-200', icon: 'text-blue-500' },
};

export default function BotKnowledgeBase({ botKey, title, description, accentColor = 'orange', materiais, localizacoes, contatos, empreendimentos, onChanged }) {
  const accent = ACCENT_CLASSES[accentColor] || ACCENT_CLASSES.orange;

  return (
    <div className={`bg-white rounded-xl border-2 border-dashed ${accent.border} shadow-sm overflow-hidden`}>
      <div className={`p-4 ${accent.header} border-b flex items-center gap-3`}>
        <Paperclip size={18} className={accent.icon} />
        <div>
          <h3 className="font-bold text-gray-800">Base de Conhecimento — {title}</h3>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </div>
      <div className="p-6 space-y-8">
        <MateriaisSection botKey={botKey} materiais={materiais || []} empreendimentos={empreendimentos} onChanged={onChanged} />
        <div className="border-t border-gray-100" />
        <LocalizacoesSection botKey={botKey} localizacoes={localizacoes || []} onChanged={onChanged} />
        <div className="border-t border-gray-100" />
        <ContatosSection botKey={botKey} contatos={contatos || []} onChanged={onChanged} />
      </div>
    </div>
  );
}
